import Button, { Colors } from "@common/components/Button";
import Grid from '@mui/material/Grid2';
import RightDrawer from "@common/components/RightDrawer";
import { WithGrow } from "@common/components/WithGrow";
import { useSimpleReducer } from "@common/hooks";
import { getDefaultDates } from "@common/utils/localTime.utils";
import { Tab, Tabs } from "@mui/material";
import { useAuthenticatedUser } from "@services/user/hooks/useAuthenticatedUser";
import React, { useCallback, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useSearchParams } from "react-router-dom";
import { SearchContext } from "../../context/search";
import { SearchField } from "../Field";
import { getSearchOrder, searchFields } from "../const";
import { TIME_FILTER_SEARCH_KEY as MEETING_TIME_FILTER_SEARCH_KEY } from "@common/utils/filters/mappers/meetings";
import { TIME_FILTER_SEARCH_KEY as TICKET_TIME_FILTER_SEARCH_KEY } from "@common/utils/filters/mappers/tickets";
import { SERVICE_EXPIRATION_TIME_FILTER_SEARCH_KEY } from "@common/utils/filters/mappers/rooms";
import {
  LEASE_EXPIRATION_TIME_FILTER_SEARCH_KEY,
  WARRANTY_EXPIRATION_TIME_FILTER_SEARCH_KEY,
} from "@common/utils/filters/mappers/devices";
import TimeButton from "@pages/Dashboard/WidgetEdit/FilterPanel/FilterSections/Section/components/time";
import { ReducerState } from "..";
import { getAssignees } from "@services/meetings";
import { Assignee } from "@services/meetings/assignee";
import { canViewMeeting, hasAssetTaggingLicense } from "@common/utils/user";
import { searchParamsToSearchObject } from "@common/utils/filters/mappers/common";
import { useInstanceSelectorProps } from "@pages/Assets/common/const";
import { useSearchNavigation } from "../hook/useSearchNavigation";
import { getSearchTypeFromLocation } from "../hook/useInitializeSearchType";

export type SearchDrawerProps = {
  open: boolean;
  type?: UserWidgetType | UserOtherType;
  filterState?: ReducerState;
};

const SearchDrawer = (props: SearchDrawerProps) => {
  const { open, filterState } = props;
  const context = useContext(SearchContext);
  const { t } = useTranslation();
  const [formState, dispatch] = useSimpleReducer<any>({});
  const [user] = useAuthenticatedUser();
  const [producersList, setProducersList] = useState<Assignee[]>([]);
  const [params] = useSearchParams();
  const { name = "/" } = useInstanceSelectorProps();
  const searchTypeFromLocation = getSearchTypeFromLocation();
  const searchType =
    searchTypeFromLocation === context.searchType
      ? context.searchType
      : undefined;
  const formData = searchParamsToSearchObject(params, searchType);
  const location = useLocation();
  const locationState =
    (location.state as {
      previousContext?: string;
    }) ?? {};

  const searchNavigate = useSearchNavigation({
    context,
    pathname: name,
    previousContext: locationState.previousContext,
  });

  const fieldList = searchFields[context.searchType].filter(({ key }) => {
    return !(key === "tags" && !hasAssetTaggingLicense(user?.account));
  });

  useEffect(() => {
    dispatch("reset");
  }, [context.searchType]);

  useEffect(() => {
    if (!filterState) return;
    const { search = "", currentField, additionalSearch } = filterState;
    if (open) {
      dispatch("reset");
      if (search.length > 0) {
        dispatch({ [currentField]: search });

        if (additionalSearch) {
          dispatch({ additionalSearch });
        }
      } else {
        dispatch(formData);
      }
    }
  }, [open]);
  const getProducersList = async () => {
    if (canViewMeeting(user)) {
      const producersList = await getAssignees();
      setProducersList(producersList);
    }
  };
  const searchParams = formState;
  const handleSearch = useCallback(async () => {
    const defaultDate = getDefaultDates();

    switch (context.searchType) {
      case "Meetings":
        if (
          searchParams[MEETING_TIME_FILTER_SEARCH_KEY] === undefined ||
          searchParams[MEETING_TIME_FILTER_SEARCH_KEY] === "Custom time period"
        ) {
          searchParams["start.after"] =
            formState[`${MEETING_TIME_FILTER_SEARCH_KEY}-customDate`]
              ?.startDate || defaultDate.start.getTime();
          searchParams["end.before"] =
            formState[`${MEETING_TIME_FILTER_SEARCH_KEY}-customDate`]
              ?.endDate || defaultDate.end.getTime();
          searchParams[MEETING_TIME_FILTER_SEARCH_KEY] = "Custom time period";

          delete formState[`${MEETING_TIME_FILTER_SEARCH_KEY}-customDate`];
        }
        if (searchParams["assignee"]) {
          if (searchParams["assignee"] === "ASSIGNED_TO") {
            searchParams["producerIds"] = formState["additionalSearch"];
          }
          searchParams["producerSearchType"] = formState["assignee"];
          delete formState["additionalSearch"];
          delete formState["assignee"];
        }
        break;
      case "Tickets":
        if (
          searchParams[TICKET_TIME_FILTER_SEARCH_KEY] === undefined ||
          searchParams[TICKET_TIME_FILTER_SEARCH_KEY] === "Custom time period"
        ) {
          searchParams["start.after"] =
            formState[`${TICKET_TIME_FILTER_SEARCH_KEY}-customDate`]
              .startDate || defaultDate.start.getTime();
          searchParams["end.before"] =
            formState[`${TICKET_TIME_FILTER_SEARCH_KEY}-customDate`].endDate ||
            defaultDate.end.getTime();
          searchParams[TICKET_TIME_FILTER_SEARCH_KEY] = "Custom time period";
          delete formState[`${TICKET_TIME_FILTER_SEARCH_KEY}-customDate`];
        }
        break;
      case "Rooms":
        if (
          searchParams[SERVICE_EXPIRATION_TIME_FILTER_SEARCH_KEY] ===
          "Custom time period"
        ) {
          searchParams["details.serviceExpirationDate.after"] =
            formState[`${SERVICE_EXPIRATION_TIME_FILTER_SEARCH_KEY}-customDate`]
              .startDate || defaultDate.start.getTime();
          searchParams["details.serviceExpirationDate.before"] =
            formState[`${SERVICE_EXPIRATION_TIME_FILTER_SEARCH_KEY}-customDate`]
              .endDate || defaultDate.end.getTime();

          delete formState[
            `${SERVICE_EXPIRATION_TIME_FILTER_SEARCH_KEY}-customDate`
          ];
        }
        break;
      case "Devices":
        if (
          searchParams[LEASE_EXPIRATION_TIME_FILTER_SEARCH_KEY] ===
          "Custom time period"
        ) {
          searchParams["details.leaseExpirationDate.after"] =
            formState[`${LEASE_EXPIRATION_TIME_FILTER_SEARCH_KEY}-customDate`]
              .startDate || defaultDate.start.getTime();
          searchParams["details.leaseExpirationDate.before"] =
            formState[`${LEASE_EXPIRATION_TIME_FILTER_SEARCH_KEY}-customDate`]
              .endDate || defaultDate.end.getTime();

          delete formState[
            `${LEASE_EXPIRATION_TIME_FILTER_SEARCH_KEY}-customDate`
          ];
        }
        if (
          searchParams[WARRANTY_EXPIRATION_TIME_FILTER_SEARCH_KEY] ===
          "Custom time period"
        ) {
          searchParams["details.warrantyExpirationDate.after"] =
            formState[
              `${WARRANTY_EXPIRATION_TIME_FILTER_SEARCH_KEY}-customDate`
            ].startDate || defaultDate.start.getTime();
          searchParams["details.warrantyExpirationDate.before"] =
            formState[
              `${WARRANTY_EXPIRATION_TIME_FILTER_SEARCH_KEY}-customDate`
            ].endDate || defaultDate.end.getTime();

          delete formState[
            `${WARRANTY_EXPIRATION_TIME_FILTER_SEARCH_KEY}-customDate`
          ];
        }
        break;
      default:
        break;
    }
    searchNavigate(searchParams);

    dispatch("reset");
    context.closeDrawer();
    context.closeSearch();
  }, [formState, context.searchType]);

  const handleClear = useCallback(() => {
    dispatch("reset");
  }, [formState]);

  const handleChange = (
    key: keyof typeof formState,
    value: string,
    payload?: FilterDefinition["payload"]
  ) => {
    payload
      ? dispatch({ [key]: value, [`${key.toString()}-customDate`]: payload })
      : dispatch({ [key]: value });
  };

  const handleMultiChange = (key: FilterTypes, values: string[]) =>
    dispatch({ [key]: values });

  const getAutoCompleteValues = (key: FilterTypes) => {
    if (formState[key]) return formState[key] as string[];
    return [];
  };

  const handleClickClear = (key: keyof typeof formState) => {
    dispatch({ [key]: undefined });
  };

  useEffect(() => {
    if (open) {
      getProducersList();
    }
  }, [open]);

  useEffect(() => {
    if (open && !formData["time"]) {
      if (
        (filterState?.currentField.startsWith("time") &&
          filterState.search === "Custom time period") ||
        (filterState?.currentField !== "time" &&
          (context.searchType === "Meetings" ||
            context.searchType === "Tickets"))
      ) {
        const defaultDate = getDefaultDates();
        handleChange(
          filterState?.currentField.startsWith("time")
            ? filterState?.currentField
            : "time",
          "Custom time period",
          {
            startDate: filterState?.startDate ?? defaultDate.start.getTime(),
            endDate: filterState?.endDate ?? defaultDate.end.getTime(),
          }
        );
      }
    }
  }, [open]);
  useEffect(() => {
    if (context.searchType === "Meetings" || context.searchType === "Tickets") {
      const defaultDate = getDefaultDates();
      handleChange("time", "Custom time period", {
        startDate: filterState?.startDate ?? defaultDate.start.getTime(),
        endDate: filterState?.endDate ?? defaultDate.end.getTime(),
      });
    }
  }, [context.searchType]);

  const formatFormStateToFilter = (key: string, formState: any) => ({
    filterType: key,
    filterValue: formState[key],
    payload: formState[`${key}-customDate`],
  });

  const handleAdditionalMultiChange = (key: FilterTypes, values: string[]) =>
    dispatch({ additionalSearch: values, errorText: undefined });

  const getAutoCompleteAdditionalValues = () => {
    if (formState.additionalSearch && Array.isArray(formState.additionalSearch))
      return formState.additionalSearch;
    return [];
  };

  return (
    <RightDrawer
      open={open}
      onClose={context.closeDrawer}
      title={"Advanced Search"}
      width="40rem"
      testId="advanced-search-drawer"
      closeButton={
        <Grid
          sx={(theme) => ({
            "&>.MuiButton-root": { marginRight: theme.spacing(1) },
          })}
        >
          <Button
            color="white"
            onClick={() => handleSearch()}
            data-testid="search-button"
          >
            Search
          </Button>
          <Button
            color="greyed"
            variant="outlined"
            onClick={() => handleClear()}
            data-testid="clear-button"
          >
            Clear
          </Button>
          <Button
            color="greyed"
            variant="outlined"
            onClick={() => context.closeDrawer()}
            data-testid="cancel-button"
          >
            Cancel
          </Button>
        </Grid>
      }
    >
      <WithGrow
        direction="column"
        fixed={
          <>
            <Tabs
              value={context.searchType}
              onChange={(_, searchType) => context.dispatch({ searchType })}
              variant="scrollable"
              visibleScrollbar
              scrollButtons={false}
            >
              {getSearchOrder(user).map((s) => (
                <Tab key={s} label={t(s)} value={s} />
              ))}
            </Tabs>
          </>
        }
      >
        <Grid
          container
          direction="column"
          wrap="nowrap"
          sx={(theme) => ({
            padding: theme.spacing(2),
            height: "100%",
            overflowY: "scroll",
            overflowX: "hidden",
          })}
        >
          {fieldList
            .sort((a, b) => a.label.localeCompare(b.label))
            .map(({ label, key }) => (
              <React.Fragment key={key}>
                <SearchField
                  entity={formState}
                  value={formState[key] ?? ""}
                  fullWidth
                  margin="dense"
                  property={key}
                  label={t(label)}
                  key={key}
                  onChange={(evt) => handleChange(key, evt.target.value)}
                  onEnterKey={handleSearch}
                  handleAutoComplete={handleMultiChange}
                  getAutoCompleteValues={getAutoCompleteValues}
                  handleClickSelectClear={() => handleClickClear(key)}
                />
                {key === "assignee" &&
                  context.searchType === "Meetings" &&
                  formState[key] === "ASSIGNED_TO" && (
                    <SearchField
                      fullWidth
                      margin="dense"
                      value={formState.additionalSearch as string}
                      property={"producerIds"}
                      label={t("Assigned to")}
                      key={"producerIds"}
                      onChange={(evt) =>
                        dispatch({
                          search: formState.search,
                          additionalSearch: evt.target.value,
                          errorText: undefined,
                        })
                      }
                      getAutoCompleteValues={getAutoCompleteAdditionalValues}
                      handleAutoComplete={handleAdditionalMultiChange}
                      handleClickSelectClear={() => handleClickClear(key)}
                      isAssignedTo={true}
                      producersList={producersList}
                    />
                  )}

                {key.startsWith("time") &&
                  Object.keys(formState).some(
                    (k: any) =>
                      k === key && formState[k] === "Custom time period"
                  ) && (
                    <TimeButton
                      buttonProps={{
                        chip: true,
                        activeColor: "white" as Colors,
                        onClick: () =>
                          handleChange(
                            key as FilterTypes,
                            "Custom time period"
                          ),
                        active:
                          Object.keys(formState).some(
                            (k: any) =>
                              k === key && formState[k] === "Custom time period"
                          ) ?? false,
                        children: "Choose the time period",
                        "data-testid": "custom-period-button",
                      }}
                      key={"Custom time period"}
                      onFilterChange={handleChange}
                      filters={[formatFormStateToFilter(key, formState)]}
                      filterType={key}
                      filterTypeKey={key as FilterTypes}
                    />
                  )}
              </React.Fragment>
            ))}
        </Grid>
      </WithGrow>
    </RightDrawer>
  );
};

export default SearchDrawer;
